import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Delete, Loader2 } from "lucide-react";

export function PinLock() {
  const { unlock } = useAuth();
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(false);
  const [shake, setShake] = useState(false);

  const submit = useCallback(
    async (value) => {
      setChecking(true);
      const res = await unlock(value);
      setChecking(false);
      if (!res.ok) {
        setError(res.error);
        setShake(true);
        setTimeout(() => setShake(false), 500);
        setPin("");
      }
    },
    [unlock]
  );

  useEffect(() => {
    if (pin.length === 4 && !checking) submit(pin);
  }, [pin, checking, submit]);

  const press = (d) => {
    setError("");
    setPin((p) => (p.length < 4 ? p + d : p));
  };
  const back = () => {
    setError("");
    setPin((p) => p.slice(0, -1));
  };

  const keys = ["1", "2", "3", "4", "5", "6", "7", "8", "9"];

  return (
    <div className="min-h-screen flex items-center justify-center p-5 relative overflow-hidden bg-[#152E22]">
      <div
        className="absolute inset-0 opacity-[0.12] bg-cover bg-center"
        style={{ backgroundImage: "url('https://images.unsplash.com/photo-1515446134809-993c501ca304?crop=entropy&cs=srgb&fm=jpg&q=85')" }}
      />
      <div className="relative w-full max-w-xs animate-fade-up">
        <div className="flex flex-col items-center mb-8">
          <span className="h-16 w-16 rounded-2xl overflow-hidden mb-4 shadow-lg ring-2 ring-white/20">
            <img src="/logo.jpg" alt="Affordable Stump Grinding" className="h-full w-full object-cover" />
          </span>
          <h1 className="font-headline text-xl font-bold text-white tracking-tight">Enter PIN</h1>
          <p className="text-emerald-200/70 text-sm mt-1">Affordable Stump Grinding</p>
        </div>

        <div className={`flex justify-center gap-4 mb-3 ${shake ? "animate-[wiggle_0.4s]" : ""}`} data-testid="pin-dots">
          {[0, 1, 2, 3].map((i) => (
            <span
              key={i}
              className={`h-4 w-4 rounded-full border-2 transition-all ${
                pin.length > i ? "bg-[#D97706] border-[#D97706] scale-110" : "border-white/40"
              }`}
            />
          ))}
        </div>

        <div className="h-6 text-center mb-3">
          {checking ? (
            <Loader2 className="h-4 w-4 animate-spin text-white/70 mx-auto" />
          ) : error ? (
            <p data-testid="pin-error" className="text-sm text-red-300">{error}</p>
          ) : null}
        </div>

        <div className="grid grid-cols-3 gap-3">
          {keys.map((k) => (
            <button
              key={k}
              data-testid={`pin-key-${k}`}
              onClick={() => press(k)}
              className="h-16 rounded-2xl bg-white/10 hover:bg-white/20 active:scale-95 text-white text-2xl font-headline font-semibold transition-all"
            >
              {k}
            </button>
          ))}
          <span />
          <button
            data-testid="pin-key-0"
            onClick={() => press("0")}
            className="h-16 rounded-2xl bg-white/10 hover:bg-white/20 active:scale-95 text-white text-2xl font-headline font-semibold transition-all"
          >
            0
          </button>
          <button
            data-testid="pin-key-back"
            onClick={back}
            className="h-16 rounded-2xl bg-transparent hover:bg-white/10 active:scale-95 text-white flex items-center justify-center transition-all"
          >
            <Delete className="h-6 w-6" />
          </button>
        </div>
      </div>
    </div>
  );
}

export function LockGate({ children }) {
  const { user, pinEnabled, locked } = useAuth();
  if (user && typeof user === "object") {
    if (pinEnabled === null) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-[#F8F6F0]">
          <Loader2 className="h-6 w-6 animate-spin text-[#1B3B2B]" />
        </div>
      );
    }
    if (locked) return <PinLock />;
  }
  return children;
}
